import {
  BaseAnalysisEngine,
  BaseAnalysisResult,
  isOfType,
} from "./baseAnalysisEngine";
import { ReactElement } from "react";
import { TFunction } from "react-i18next";
import { ResultDashboard } from "../components/molecules/ResultDashboard";
import { ResultCo1Demo } from "../components/molecules/ResultCo1Demo";
import {
  AnalysisEngineType,
  ApiEngineType,
  MeanResult,
  ResultDashboardData,
  ResultMainParam,
  ResultSubParam,
} from "../types";
import { appAxios } from "../utils/appAxios";

export type AnalysisResult = BaseAnalysisResult & {
  co1_result: string;
  co1_score: number;
  co1_missing_info: Record<string, number>;
  co1_sub_parameters: Record<string, object>;
};

export type ConvertResultType = {
  Co1_result: string;
  Co1_score: string | number;
  ParamA_LVP_S: string | number;
  ParamA_LVP_J: string | number;
  ParamA_LVP_H: string | number;
  ParamB_LVP_S: string | number;
  ParamB_LVP_J: string | number;
  ParamB_LVP_H: string | number;
  ParamC_LVP_S: string | number;
  ParamC_LVP_J: string | number;
  ParamC_LVP_H: string | number;
  ParamD_LVP_S: string | number;
  ParamD_LVP_J: string | number;
  ParamD_LVP_H: string | number;
  ParamD_LVP_D: string | number;
  ParamD_LVP_DR: string | number;
  DSTP_AS: string | number;
  DSTP_PS: string | number;
  DSTP_PSA: string | number;
  DSTP_PSS: string | number;
  DSTP_DR: string | number;
};

const SUB_PARAMS_TITLE_LIST = ["Result.Dementia_score"];

const DEFAULT_DASHBOARD_MAIN_PARAM: ResultMainParam = {
  title: "Result.Dementia_rank",
  // border: "solid 3px #EFB6CB",
  value: undefined,
};

const ERROR_DASHBOARD_DATA: ResultDashboardData = {
  mainParam: DEFAULT_DASHBOARD_MAIN_PARAM,
  subParams: [
    {
      title: SUB_PARAMS_TITLE_LIST[0],
      value: undefined,
    },
  ],
  supplementaryInfo: [],
  diagramIndex: [],
  diagramParams: [],
};

export function ConvertResult({
  result,
}: {
  result: AnalysisResult;
}): ConvertResultType {
  if (!result) {
    return {
      Co1_result: "-",
      Co1_score: "-",
      ParamA_LVP_S: "-",
      ParamA_LVP_J: "-",
      ParamA_LVP_H: "-",
      ParamB_LVP_S: "-",
      ParamB_LVP_J: "-",
      ParamB_LVP_H: "-",
      ParamC_LVP_S: "-",
      ParamC_LVP_J: "-",
      ParamC_LVP_H: "-",
      ParamD_LVP_S: "-",
      ParamD_LVP_J: "-",
      ParamD_LVP_H: "-",
      ParamD_LVP_D: "-",
      ParamD_LVP_DR: "-",
      DSTP_AS: "-",
      DSTP_PS: "-",
      DSTP_PSA: "-",
      DSTP_PSS: "-",
      DSTP_DR: "-",
    };
  }
  type SubItem01 = {
    ParamA?: {
      LVP_S?: number;
      LVP_J?: number;
      LVP_H?: number;
    };
    ParamB?: {
      LVP_S?: number;
      LVP_J?: number;
      LVP_H?: number;
    };
    ParamC?: {
      LVP_S?: number;
      LVP_J?: number;
      LVP_H?: number;
    };
    ParamD: {
      LVP_S?: number;
      LVP_J?: number;
      LVP_H?: number;
      LVP_D?: number;
      LVP_DR: number;
    };
  };

  // type SubItem02 = {
  //   PAP?: number;
  // };

  type SubItem03 = {
    DSTP_AS?: number;
    DSTP_PS?: number;
    DSTP_PSA?: number;
    DSTP_PSS?: number;
    DSTP_DR: number;
  };

  // missing_infoがあるとco1_sub_parametersのデータ構造が変わる為、
  // データ型からサブパラメーターの種類を取得する
  const sampleSubItem01: SubItem01 = { ParamD: { LVP_DR: 0 } };
  // const sampleSubItem02: SubItem02 = { PAP: 0 };
  const sampleSubItem03: SubItem03 = { DSTP_DR: 0 };

  const isSubItem01 = (item: unknown): item is SubItem01 =>
    isOfType<SubItem01>(item, sampleSubItem01);
  const isSubItem03 = (item: unknown): item is SubItem03 =>
    isOfType<SubItem03>(item, sampleSubItem03);

  const DEFAULT_VALUE = 0.0;

  // 「長母音」
  const subItem01 = Object.values(result.co1_sub_parameters).find(
    (subParameters) => {
      return isSubItem01(subParameters);
    }
  ) as SubItem01 | undefined;

  const ParamA_LVP_S = subItem01?.ParamA?.LVP_S ?? DEFAULT_VALUE; // 前半Shimmer
  const ParamA_LVP_J = subItem01?.ParamA?.LVP_J ?? DEFAULT_VALUE; // 前半Jitter
  const ParamA_LVP_H = subItem01?.ParamA?.LVP_H ?? DEFAULT_VALUE; // 前半HNR
  const ParamB_LVP_S = subItem01?.ParamB?.LVP_S ?? DEFAULT_VALUE; // 中盤Shimmer
  const ParamB_LVP_J = subItem01?.ParamB?.LVP_J ?? DEFAULT_VALUE; // 中盤Jitter
  const ParamB_LVP_H = subItem01?.ParamB?.LVP_H ?? DEFAULT_VALUE; // 中盤HNR
  const ParamC_LVP_S = subItem01?.ParamC?.LVP_S ?? DEFAULT_VALUE; // 後半Shimmer
  const ParamC_LVP_J = subItem01?.ParamC?.LVP_J ?? DEFAULT_VALUE; // 後半Jitter
  const ParamC_LVP_H = subItem01?.ParamC?.LVP_H ?? DEFAULT_VALUE; // 後半HNR
  const ParamD_LVP_S = subItem01?.ParamD?.LVP_S ?? DEFAULT_VALUE; // 全体Shimmer
  const ParamD_LVP_J = subItem01?.ParamD?.LVP_J ?? DEFAULT_VALUE; // 全体Jitter
  const ParamD_LVP_H = subItem01?.ParamD?.LVP_H ?? DEFAULT_VALUE; // 全体HNR
  const ParamD_LVP_D = subItem01?.ParamD?.LVP_D ?? DEFAULT_VALUE; // 全体DTW
  const ParamD_LVP_DR = subItem01?.ParamD?.LVP_DR ?? DEFAULT_VALUE; // 全体Duration

  // 「パタカ」
  const subItem03 = Object.values(result.co1_sub_parameters).find(
    (subParameters) => {
      return isSubItem03(subParameters);
    }
  ) as SubItem03 | undefined;

  const DSTP_AS = subItem03?.DSTP_AS ?? DEFAULT_VALUE; // All Scope
  const DSTP_PS = subItem03?.DSTP_PS ?? DEFAULT_VALUE; // Peak SD
  const DSTP_PSA = subItem03?.DSTP_PSA ?? DEFAULT_VALUE; // 発声の速さ
  const DSTP_PSS = subItem03?.DSTP_PSS ?? DEFAULT_VALUE; // 発声リズムの安定性
  const DSTP_DR = subItem03?.DSTP_DR ?? DEFAULT_VALUE; // 「パタカ」の長さ

  const formatParameter = (param: number, fractionDigits: number): string => {
    return isNaN(param) ? "-" : param.toFixed(fractionDigits);
  };

  return {
    Co1_result: result.co1_result,
    Co1_score: result.co1_score,
    ParamA_LVP_S: formatParameter(ParamA_LVP_S, 4),
    ParamA_LVP_J: formatParameter(ParamA_LVP_J, 4),
    ParamA_LVP_H: formatParameter(ParamA_LVP_H, 2),
    ParamB_LVP_S: formatParameter(ParamB_LVP_S, 4),
    ParamB_LVP_J: formatParameter(ParamB_LVP_J, 4),
    ParamB_LVP_H: formatParameter(ParamB_LVP_H, 2),
    ParamC_LVP_S: formatParameter(ParamC_LVP_S, 4),
    ParamC_LVP_J: formatParameter(ParamC_LVP_J, 4),
    ParamC_LVP_H: formatParameter(ParamC_LVP_H, 2),
    ParamD_LVP_S: formatParameter(ParamD_LVP_S, 4),
    ParamD_LVP_J: formatParameter(ParamD_LVP_J, 4),
    ParamD_LVP_H: formatParameter(ParamD_LVP_H, 2),
    ParamD_LVP_D: formatParameter(ParamD_LVP_D, 1),
    ParamD_LVP_DR: formatParameter(ParamD_LVP_DR, 1),
    DSTP_AS: formatParameter(DSTP_AS, 1),
    DSTP_PS: formatParameter(DSTP_PS, 1),
    DSTP_PSA: formatParameter(DSTP_PSA, 1),
    DSTP_PSS: formatParameter(DSTP_PSS, 1),
    DSTP_DR: formatParameter(DSTP_DR, 1),
  };
}

// /analysis/co1のレスポンスにおけるCo1スコアのパス
export const AnalysisResultCo1ScorePath = "co1_score";

interface EngineProps {
  _analysisType?: AnalysisEngineType | null;
}

export class Co1Engine extends BaseAnalysisEngine<AnalysisResult> {
  _resultCaches: Record<string, AnalysisResult> = {};
  _engineType: ApiEngineType = "co1";
  _apiPath = "/analysis/co1";
  _analysisType: AnalysisEngineType | null = null;

  constructor(props?: EngineProps) {
    super();
    if (props && props._analysisType) {
      this._analysisType = props._analysisType;
    }
  }

  async getAverage(companyId?: string): Promise<MeanResult | null> {
    const params = {
      engine: "co1",
      data_path: "co1_score",
      company_id: companyId,
    };
    try {
      const response = await appAxios.get("/analysis/statistics/mean", {
        params,
      });
      return response.data;
    } catch {
      return null;
    }
  }

  private static convertToDashboardData(
    result: AnalysisResult
  ): ResultDashboardData {
    const mainParam: ResultMainParam = {
      ...DEFAULT_DASHBOARD_MAIN_PARAM,
      value: result.co1_result,
    };
    const subParams: ResultSubParam[] = [
      {
        title: SUB_PARAMS_TITLE_LIST[0],
        value: result.co1_score,
      },
    ];

    return {
      mainParam,
      subParams,
      supplementaryInfo: [],
      diagramIndex: [],
      diagramParams: [],
    };
  }

  async renderResult(
    key: number,
    fileId: string,
    t: TFunction
  ): Promise<ReactElement> {
    const result = await this.getResult(fileId);
    return this._analysisType === "Co1Demo" ? (
      <ResultCo1Demo
        key={key}
        title={t("Result.Dementia_analysis_result")}
        co1_result={result ? result.co1_result : ""}
      />
    ) : (
      <ResultDashboard
        key={key}
        title={t("Result.Dementia_analysis_result") + "(Co-1)"}
        result={
          result
            ? Co1Engine.convertToDashboardData(result)
            : ERROR_DASHBOARD_DATA
        }
      />
    );
  }
}
